export type IncomePovertyRaceRow = {
  race_or_ethnicity?: string;
  race?: string;
  median_income?: number | null;
  percentage?: number | null;
};

export type IncomePovertyFacts = {
  latestYear: number | null;
  lowestIncome: number | null;
  highestIncome: number | null;
  lowestIncomeGroup: string | null;
  highestIncomeGroup: string | null;
  lowestPovertyRate: number | null;
  highestPovertyRate: number | null;
  lowestPovertyGroup: string | null;
  highestPovertyGroup: string | null;
  incomeRatio: number | null;
  povertyRatio: number | null;
};

function pickExtreme<T extends { value: number; label: string }>(
  rows: T[],
  mode: 'min' | 'max'
): { value: number; label: string } | null {
  const valid = rows.filter((r) => Number.isFinite(r.value) && r.value > 0);
  if (!valid.length) return null;
  return valid.reduce((acc, row) => {
    if (mode === 'min') return row.value < acc.value ? row : acc;
    return row.value > acc.value ? row : acc;
  });
}

export function buildIncomePovertyFacts(params: {
  latestYear: number | null;
  incomeRows: IncomePovertyRaceRow[];
  povertyRows: IncomePovertyRaceRow[];
}): IncomePovertyFacts {
  const incomeMapped = (params.incomeRows || [])
    .map((r) => ({
      label: String(r.race_or_ethnicity || '').trim(),
      value: Number(r.median_income),
    }))
    .filter((r) => r.label && Number.isFinite(r.value) && r.value > 0);

  const povertyMapped = (params.povertyRows || [])
    .map((r) => ({
      label: String(r.race || r.race_or_ethnicity || '').trim(),
      value: Number(r.percentage),
    }))
    .filter((r) => r.label && Number.isFinite(r.value) && r.value >= 0);

  const lowInc = pickExtreme(incomeMapped, 'min');
  const highInc = pickExtreme(incomeMapped, 'max');
  const lowPov = pickExtreme(povertyMapped, 'min');
  const highPov = pickExtreme(povertyMapped, 'max');

  const incomeRatio =
    lowInc && highInc && lowInc.value > 0 ? highInc.value / lowInc.value : null;
  const povertyRatio =
    lowPov && highPov && lowPov.value > 0 ? highPov.value / lowPov.value : null;

  return {
    latestYear: params.latestYear,
    lowestIncome: lowInc?.value ?? null,
    highestIncome: highInc?.value ?? null,
    lowestIncomeGroup: lowInc?.label ?? null,
    highestIncomeGroup: highInc?.label ?? null,
    lowestPovertyRate: lowPov?.value ?? null,
    highestPovertyRate: highPov?.value ?? null,
    lowestPovertyGroup: lowPov?.label ?? null,
    highestPovertyGroup: highPov?.label ?? null,
    incomeRatio,
    povertyRatio,
  };
}
